import { useFormik } from 'formik';
import { useCallback, useId, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { object } from 'yup';

import {
  getErrorMessage,
  PartBaqData,
  PartViewerPageData,
  PREFERRED_UNIT_SYSTEMS,
  useUpdatePartForBaq,
} from '@frontend/api';
import {
  Button,
  Flex,
  FormControl,
  Heading,
  HStack,
  InputGroup,
  InputLeftAddon,
  InputRightAddon,
  Tooltip,
  useToast,
  VStack,
  Stat,
  Text,
  Box,
  FormErrorMessage,
  Skeleton,
} from '@frontend/components';

import {
  MathInput,
  MathInputField,
  useMathInputValidationSchema,
} from 'components/MathInput';

import {
  formatMeasurement,
  UNITS_BY_SYSTEM_AND_MEASURE,
} from 'constants/unitOfMeasure';

import {
  getActiveComponentId,
  getIsQuoteItemLocked,
} from 'modules/quotePartViewer/data/reducer';
import { broadcastPartDimensionsChange } from 'modules/quotes/quoteDetailsPage/hooks/useSyncQpvAndBaqTabs';
import { DisabledPartDescriptorTooltipLabel } from 'modules/quotes/quoteItems/components/util';
import { partDetails } from 'modules/viewerPage/data/actions';
import {
  getActivePart,
  getIsLoading,
  getIsInAFinalizedQuote,
  getIsInATrashedQuote,
  getIsModifiable,
} from 'modules/viewerPage/data/reducer';

import { getPreferredUnitSystem } from 'store/session/selectors';

import { useUnitSystemContext } from 'utils/measurement/context';

/**
 * Checks if there's a meaningful difference between two dimension values.
 * Returns true if:
 * - One is nullish and the other isn't
 * - Both are numbers and differ by more than the threshold
 */
const hasMeaningfulDifference = (
  initialValue: number | null | undefined,
  currentValue: number | null | undefined,
  threshold = 0.001,
): boolean => {
  // Check if nullish state has changed
  const initialIsNullish = initialValue == null;
  const currentIsNullish = currentValue == null;

  if (initialIsNullish !== currentIsNullish) {
    return true;
  }

  // If both are nullish, no meaningful difference
  if (initialIsNullish && currentIsNullish) {
    return false;
  }

  // Both are numbers, check if difference exceeds threshold
  return Math.abs(initialValue! - currentValue!) > threshold;
};

/**
 * Checks if any of the three dimensions have meaningfully changed.
 */
const hasAnyMeaningfulDimensionChange = (
  initial: {
    sizeX?: string | null;
    sizeY?: string | null;
    sizeZ?: string | null;
  },
  current: {
    sizeX?: string | null;
    sizeY?: string | null;
    sizeZ?: string | null;
  },
): boolean => {
  const initialX =
    initial.sizeX == null || initial.sizeX === ''
      ? null
      : Number(initial.sizeX);
  const initialY =
    initial.sizeY == null || initial.sizeY === ''
      ? null
      : Number(initial.sizeY);
  const initialZ =
    initial.sizeZ == null || initial.sizeZ === ''
      ? null
      : Number(initial.sizeZ);

  const currentX =
    current.sizeX == null || current.sizeX === ''
      ? null
      : Number(current.sizeX);
  const currentY =
    current.sizeY == null || current.sizeY === ''
      ? null
      : Number(current.sizeY);
  const currentZ =
    current.sizeZ == null || current.sizeZ === ''
      ? null
      : Number(current.sizeZ);

  return (
    hasMeaningfulDifference(initialX, currentX) ||
    hasMeaningfulDifference(initialY, currentY) ||
    hasMeaningfulDifference(initialZ, currentZ)
  );
};

const DimensionFieldsSkeleton = () => {
  return (
    <VStack spacing={1.5} align="stretch">
      <HStack justify="space-between" align="center">
        <Heading size="md">Dimensions (L-W-H)</Heading>
        <Button variant="link" size="sm" isDisabled>
          Swap units
        </Button>
      </HStack>
      <Skeleton h={5} borderRadius="md" />
    </VStack>
  );
};

const ReadOnlyDisplay = ({
  activePart,
}: {
  activePart: PartViewerPageData;
}) => {
  const { sizeX, sizeY, sizeZ } = activePart;

  const preferredUnitSystem = useSelector(getPreferredUnitSystem)!;
  const [unitSystem, setUnitSystem] = useState(preferredUnitSystem);
  const handleSwapUnits = useCallback(() => {
    setUnitSystem(
      unitSystem === PREFERRED_UNIT_SYSTEMS.IMPERIAL
        ? PREFERRED_UNIT_SYSTEMS.METRIC
        : PREFERRED_UNIT_SYSTEMS.IMPERIAL,
    );
  }, [unitSystem]);

  return (
    <VStack spacing={1} align="stretch">
      <HStack justify="space-between" align="center">
        <Heading size="md">Dimensions (L-W-H)</Heading>
        <Button variant="link" size="sm" onClick={handleSwapUnits}>
          Swap units
        </Button>
      </HStack>
      <Flex>
        <Stat size="sm" flexGrow={1} data-testid="part-detail-container">
          <Text>
            <Text
              fontWeight="bold"
              color="geometry.x"
              as="span"
              data-fs-hide="true"
            >
              {formatMeasurement({
                value: sizeX ?? null,
                unitSystem,
                includeUnits: false,
                measure: 'length',
              })}
            </Text>
            &nbsp;x&nbsp;
            <Text
              fontWeight="bold"
              color="geometry.y"
              as="span"
              data-fs-hide="true"
            >
              {formatMeasurement({
                value: sizeY ?? null,
                unitSystem,
                includeUnits: false,
                measure: 'length',
              })}
            </Text>
            &nbsp;x&nbsp;
            <Text
              fontWeight="bold"
              color="geometry.z"
              as="span"
              data-fs-hide="true"
            >
              {formatMeasurement({
                value: sizeZ ?? null,
                unitSystem,
                includeUnits: false,
                measure: 'length',
              })}
            </Text>
            &nbsp;{UNITS_BY_SYSTEM_AND_MEASURE[unitSystem].length}
          </Text>
        </Stat>
      </Flex>
      <Text color="feature.secondary" size="sm">
        Populated from model file
      </Text>
    </VStack>
  );
};

export const useUpdatePartDimensions = () => {
  const activePart = useSelector(getActivePart);
  const dispatch = useDispatch();
  const componentId = useSelector(getActiveComponentId);

  const toast = useToast();

  // Track request sequence to prevent race conditions
  const requestSequenceRef = useRef(0);
  const latestRequestIdRef = useRef(0);

  const { mutate: updatePartForBaq, isLoading: isUpdatingPartDimensions } =
    useUpdatePartForBaq(
      {
        partUuid: activePart?.uuid ?? '',
      },
      {
        queryOptions: {
          onMutate: () => {
            // Increment and track the latest request ID
            const requestId = ++requestSequenceRef.current;
            latestRequestIdRef.current = requestId;
            return { requestId } as { prev?: PartBaqData; requestId: number };
          },
          onSuccess: (data, variables, context) => {
            // Only process this response if it's the latest request
            const requestId =
              context && typeof context === 'object' && 'requestId' in context
                ? (context as { requestId: number }).requestId
                : undefined;
            if (
              requestId !== undefined &&
              requestId !== latestRequestIdRef.current
            ) {
              // This is a stale response, ignore it
              return;
            }

            broadcastPartDimensionsChange({
              context: {
                componentId: componentId ?? 0,
                partUuid: activePart?.uuid ?? '',
              },
            });
            // refresh part since the response is of a different shape than we need
            //  this returns the BAQ version of a part
            dispatch(partDetails.get({ partUuid: activePart?.uuid ?? '' }));
          },
          onError: (error) => {
            toast({
              status: 'error',
              title: 'Error updating part',
              description: getErrorMessage(error),
            });
          },
        },
      },
    );

  return {
    updatePartForBaq,
    isUpdatingPartDimensions,
  };
};

const EditableDisplay = ({
  activePart,
}: {
  activePart: PartViewerPageData;
}) => {
  const { sizeX, sizeY, sizeZ } = activePart ?? {};

  const isInAFinalizedQuote = useSelector(getIsInAFinalizedQuote);
  const isInATrashedQuote = useSelector(getIsInATrashedQuote);
  const isModifiable = useSelector(getIsModifiable);
  const isQuoteItemLocked = useSelector(getIsQuoteItemLocked);

  const canEditPartDimensions =
    !isQuoteItemLocked &&
    isModifiable &&
    !isInAFinalizedQuote &&
    !isInATrashedQuote;

  const { updatePartForBaq } = useUpdatePartDimensions();

  const { preferredUnitSystem, togglePreferredUnitSystem } =
    useUnitSystemContext();

  const unitLabel =
    preferredUnitSystem === PREFERRED_UNIT_SYSTEMS.IMPERIAL ? 'in' : 'mm';

  const formId = useId();

  const sizeValidationSchema = useMathInputValidationSchema('length', {
    positive: true,
  });

  const validationSchema = object().shape({
    sizeX: sizeValidationSchema,
    sizeY: sizeValidationSchema,
    sizeZ: sizeValidationSchema,
  });

  const initialValues = useMemo(
    () => ({
      // NB: coalescing to undefined makes component uncontrolled, so we use string here
      sizeX: sizeX?.toFixed(5) ?? '',
      sizeY: sizeY?.toFixed(5) ?? '',
      sizeZ: sizeZ?.toFixed(5) ?? '',
    }),
    [sizeX, sizeY, sizeZ],
  );

  const {
    values,
    errors,
    handleSubmit,
    handleBlur,
    handleChange,
    validateForm,
  } = useFormik({
    initialValues,
    onSubmit: (values) => {
      // Only submit if there are meaningful changes
      if (!hasAnyMeaningfulDimensionChange(initialValues, values)) {
        return;
      }
      const { sizeX, sizeY, sizeZ } = values;
      updatePartForBaq({
        sizeX: sizeX == null || sizeX === '' ? null : Number(sizeX),
        sizeY: sizeY == null || sizeY === '' ? null : Number(sizeY),
        sizeZ: sizeZ == null || sizeZ === '' ? null : Number(sizeZ),
        units: 'mm',
      });
    },
    validationSchema,
    validateOnBlur: true,
    validateOnChange: false,
    enableReinitialize: true,
  });

  const handleBlurWithSubmit = useCallback(
    async (e: React.FocusEvent<HTMLInputElement>) => {
      handleBlur(e);

      // Wait for validation to complete
      const errors = await validateForm();

      // Only submit if there are no validation errors
      if (Object.keys(errors).length === 0) {
        handleSubmit();
      }
    },
    [handleBlur, validateForm, handleSubmit],
  );

  const TooltipLabel = useMemo(
    () => (
      <DisabledPartDescriptorTooltipLabel
        action="edit geometric attributes"
        isPartInSentQuote={isInAFinalizedQuote}
        isPartInTrashedQuote={isInATrashedQuote}
        isPartInMultipleQuoteItems={!isModifiable}
        isAnotherPersonEditing={isQuoteItemLocked}
      />
    ),
    [isInAFinalizedQuote, isInATrashedQuote, isModifiable, isQuoteItemLocked],
  );

  return (
    <VStack spacing={1.5} align="stretch">
      <HStack justify="space-between" align="center">
        <Heading size="md">Dimensions (L-W-H)</Heading>
        <Button variant="link" size="sm" onClick={togglePreferredUnitSystem}>
          Swap units
        </Button>
      </HStack>
      <Tooltip isDisabled={canEditPartDimensions} label={TooltipLabel}>
        <Box as="form" id={formId} onSubmit={handleSubmit}>
          <FormControl
            isDisabled={!canEditPartDimensions}
            isInvalid={!!errors.sizeX || !!errors.sizeY || !!errors.sizeZ}
          >
            <InputGroup size="md">
              <HStack flex={1} spacing={0}>
                <MathInput
                  measure="length"
                  isRightAddonHidden={true}
                  size="md"
                  _focusWithin={{ zIndex: 1 }}
                >
                  <InputLeftAddon
                    color="geometry.x"
                    fontWeight="medium"
                    borderLeftRadius="sm"
                  >
                    X
                  </InputLeftAddon>
                  <MathInputField
                    placeholder="—"
                    data-fs-hide="true"
                    borderRadius="0"
                    id="sizeX"
                    name="sizeX"
                    value={values.sizeX}
                    onChange={handleChange}
                    onBlur={handleBlurWithSubmit}
                    maxPrecision={5}
                    onEnter={handleBlur}
                  />
                </MathInput>
                <MathInput
                  measure="length"
                  isRightAddonHidden={true}
                  size="md"
                  borderRight="none"
                  _focusWithin={{ zIndex: 1 }}
                >
                  <InputLeftAddon
                    color="geometry.y"
                    fontWeight="medium"
                    borderLeftWidth="0"
                  >
                    Y
                  </InputLeftAddon>
                  <MathInputField
                    placeholder="—"
                    data-fs-hide="true"
                    borderRightRadius="0"
                    id="sizeY"
                    name="sizeY"
                    value={values.sizeY}
                    onChange={handleChange}
                    onBlur={handleBlurWithSubmit}
                    maxPrecision={5}
                    onEnter={handleBlur}
                  />
                </MathInput>
                <MathInput
                  measure="length"
                  isRightAddonHidden={true}
                  size="md"
                  borderRight="none"
                  _focusWithin={{ zIndex: 1 }}
                >
                  <InputLeftAddon
                    color="geometry.z"
                    fontWeight="medium"
                    borderLeftWidth="0"
                  >
                    Z
                  </InputLeftAddon>
                  <MathInputField
                    placeholder="—"
                    data-fs-hide="true"
                    borderRightRadius="0"
                    id="sizeZ"
                    name="sizeZ"
                    value={values.sizeZ}
                    onChange={handleChange}
                    onBlur={handleBlurWithSubmit}
                    maxPrecision={5}
                    onEnter={handleBlur}
                  />
                </MathInput>
              </HStack>
              <InputRightAddon borderRightRadius="sm">
                {unitLabel}
              </InputRightAddon>
            </InputGroup>
            <FormErrorMessage>
              {errors.sizeX || errors.sizeY || errors.sizeZ}
            </FormErrorMessage>
          </FormControl>
        </Box>
      </Tooltip>
    </VStack>
  );
};

export const DimensionFields = () => {
  const isLoading = useSelector(getIsLoading);
  const activePart = useSelector(getActivePart);
  const { partFiles, primaryFileUuid } = activePart ?? {};

  const primaryPartFile = Array.isArray(partFiles)
    ? partFiles.find((partFile) => partFile.uuid === primaryFileUuid)
    : undefined;

  const showUneditableMode = primaryPartFile?.hasGeometry;
  const showSkeleton = isLoading || !activePart || !primaryPartFile;

  if (showSkeleton) {
    return <DimensionFieldsSkeleton />;
  }

  return showUneditableMode ? (
    <ReadOnlyDisplay activePart={activePart} />
  ) : (
    <EditableDisplay activePart={activePart} />
  );
};
